import { useGoals } from 'features/goals';
import { useMemo } from 'react';
import { useUser } from 'shared/hooks/use-user';
import { GetGoalsOptions } from 'shared/services/backend-api';
import { Goal } from 'shared/types/goal';
import { sortGoals } from 'shared/utils/sort-goals';

export const useSortedGoals = (options?: GetGoalsOptions) => {
  const user = useUser();
  const goals = useGoals(options);

  const goalSorting = user?.settings?.goalSorting;
  const sortMode = user?.settings?.goalSortingMode;

  return useMemo(() => {
    if (!goals?.length) {
      return [];
    }

    const rootGoals = goals.filter((goal) => !goal.parentIds?.length);
    const sortedRootGoals = sortGoals(rootGoals, {
      allGoals: goals,
      sortMode,
      customOrder: goalSorting,
    });

    const processGoals = (parentGoals: Goal[]): Goal[] => {
      return parentGoals.flatMap((parentGoal) => {
        const childGoals = goals.filter(
          (goal) =>
            goal.parentIds?.[goal.parentIds.length - 1] === parentGoal.id,
        );

        const sortedChildGoals = sortGoals(childGoals, {
          allGoals: goals,
          sortMode: parentGoal.goalSortingMode,
          customOrder: parentGoal.goalSorting,
        });

        return [parentGoal, ...processGoals(sortedChildGoals)];
      });
    };

    return processGoals(sortedRootGoals);
  }, [goals, sortMode, goalSorting]);
};
